import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Calendar, 
  Percent, 
  DollarSign, 
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Play,
  Pause
} from 'lucide-react';
import { Card, Badge, Button } from '../../components/ui';
import { api } from '../../lib/api';

interface YieldConfig {
  id: string;
  assetId: string;
  yieldType: 'FIXED' | 'VARIABLE' | 'INDEXED';
  yieldRate: number;
  indexReference?: string;
  frequency: 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUALLY' | 'ANNUALLY' | 'AT_MATURITY';
  calculationBase: 'NOMINAL' | 'MARKET';
  taxWithholding: boolean;
  taxRates: {
    upTo180Days: number;
    upTo360Days: number;
    upTo720Days: number;
    above720Days: number;
  };
  isActive: boolean;
  nextDistributionDate?: string;
  lastDistributionDate?: string;
}

interface Asset {
  id: string;
  name: string;
  symbol: string;
  totalSupply: number;
  unitPrice: number;
  maturityDate?: string;
  status: string;
}

interface Distribution {
  id: string;
  period: string;
  totalAmount: number;
  totalHolders: number;
  status: 'PENDING' | 'CALCULATING' | 'READY' | 'DISTRIBUTING' | 'COMPLETED' | 'FAILED';
  distributedAt?: string;
  createdAt: string;
}

const frequencyLabels: Record<string, string> = {
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  SEMIANNUALLY: 'Semestral',
  ANNUALLY: 'Anual',
  AT_MATURITY: 'No Vencimento'
};

const yieldTypeLabels: Record<string, string> = {
  FIXED: 'Taxa Fixa',
  VARIABLE: 'Taxa Variável',
  INDEXED: 'Indexado'
};

export function YieldConfiguration() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [config, setConfig] = useState<Partial<YieldConfig>>({
    yieldType: 'FIXED',
    yieldRate: 0,
    frequency: 'MONTHLY',
    calculationBase: 'NOMINAL',
    taxWithholding: true,
    taxRates: {
      upTo180Days: 22.5,
      upTo360Days: 20,
      upTo720Days: 17.5,
      above720Days: 15
    },
    isActive: false
  });

  // Query para listar ativos quando não há ID selecionado
  const { data: assets, isLoading: assetsLoading } = useQuery<Asset[]>({
    queryKey: ['tokenization-assets-with-yield'],
    queryFn: async () => {
      const res = await api.get('/tokenization/assets', {
        params: { status: 'ACTIVE,MINTED' }
      });
      return res.data.data || res.data;
    },
    enabled: !id,
  });

  const { data: asset, isLoading: assetLoading } = useQuery<Asset>({
    queryKey: ['asset', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: yieldConfig, isLoading: configLoading } = useQuery<YieldConfig>({
    queryKey: ['yield-config', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}/yield-config`);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: distributions } = useQuery<Distribution[]>({
    queryKey: ['yield-distributions', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}/distributions`);
      return res.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (yieldConfig) {
      setConfig(yieldConfig);
    }
  }, [yieldConfig]);

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<YieldConfig>) => {
      if (yieldConfig?.id) {
        return api.put(`/tokenization/assets/${id}/yield-config`, data);
      }
      return api.post(`/tokenization/assets/${id}/yield-config`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yield-config', id] });
    }
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/tokenization/assets/${id}/yield-config/toggle`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yield-config', id] });
    }
  });

  const triggerDistributionMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/tokenization/assets/${id}/distributions/trigger`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yield-distributions', id] });
    }
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('pt-BR');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(config);
  };

  if (!id) {
    // Mostrar lista de ativos para selecionar
    if (assetsLoading) {
      return (
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-surface-800 rounded w-64" />
            <div className="h-96 bg-surface-800 rounded-xl" />
          </div>
        </div>
      );
    }

    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Configuração de Rendimentos (Yield)</h1>
          <p className="text-surface-400 mt-1">Selecione um ativo para configurar a distribuição de rendimentos</p>
        </div>

        {!assets || assets.length === 0 ? (
          <Card className="p-8 text-center">
            <TrendingUp className="w-12 h-12 text-surface-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-surface-200 mb-2">Nenhum ativo disponível</h3>
            <p className="text-surface-400 mb-4">
              Você precisa ter ativos tokenizados (status ACTIVE ou MINTED) para configurar rendimentos.
            </p>
            <Button onClick={() => navigate('/admin/tokenization/assets')}>
              Ver Ativos
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {assets.map((a) => (
              <Card 
                key={a.id} 
                className="p-4 hover:bg-surface-800/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/tokenization/yield/${a.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-surface-100">{a.name}</h3>
                      <p className="text-sm text-surface-400">{a.symbol}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-surface-200">{formatCurrency(a.unitPrice)}/token</p>
                    <Badge variant={a.status === 'ACTIVE' ? 'success' : 'warning'}>
                      {a.status}
                    </Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (assetLoading || configLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="h-96 bg-surface-800 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="p-6 text-center">
        <p className="text-surface-400">Ativo não encontrado</p>
        <Button className="mt-4" onClick={() => navigate('/admin/tokenization/yield')}>
          Voltar para lista
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-surface-800 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-surface-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Configuração de Rendimentos</h1>
            <p className="text-surface-400">{asset.name} ({asset.symbol})</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {yieldConfig?.id && (
            <Button
              variant={config.isActive ? 'secondary' : 'primary'}
              onClick={() => toggleActiveMutation.mutate()}
              disabled={toggleActiveMutation.isPending}
            >
              {config.isActive ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Pausar Distribuições
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Ativar Distribuições
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {yieldConfig?.isActive ? (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div>
              <p className="font-medium text-green-400">Distribuições Ativas</p>
              <p className="text-sm text-surface-400">
                Próxima distribuição: {yieldConfig.nextDistributionDate 
                  ? formatDate(yieldConfig.nextDistributionDate) 
                  : 'A calcular'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-500" />
            <div>
              <p className="font-medium text-yellow-400">Distribuições Inativas</p>
              <p className="text-sm text-surface-400">
                Configure e ative as distribuições de rendimentos
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Yield Type */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Percent className="w-5 h-5 text-primary-400" />
                <h3 className="font-semibold text-surface-100">Tipo de Rendimento</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-surface-400 mb-1">Tipo</label>
                  <select
                    value={config.yieldType}
                    onChange={(e) => setConfig({ ...config, yieldType: e.target.value as YieldConfig['yieldType'] })}
                    className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  >
                    {Object.entries(yieldTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-surface-400 mb-1">
                    Taxa de Rendimento (% a.a.)
                  </label>
                  <input
                    type="number"
                    value={config.yieldRate || ''}
                    onChange={(e) => setConfig({ ...config, yieldRate: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                    min={0}
                    max={100}
                    step={0.01}
                  />
                </div>

                {config.yieldType === 'INDEXED' && (
                  <div className="col-span-2">
                    <label className="block text-sm text-surface-400 mb-1">Índice de Referência</label>
                    <select
                      value={config.indexReference || ''}
                      onChange={(e) => setConfig({ ...config, indexReference: e.target.value })}
                      className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                    >
                      <option value="">Selecione o índice</option>
                      <option value="CDI">CDI</option>
                      <option value="IPCA">IPCA</option>
                      <option value="SELIC">SELIC</option>
                      <option value="IGPM">IGP-M</option>
                    </select>
                    <p className="text-xs text-surface-500 mt-1">
                      A taxa será aplicada sobre o índice selecionado (ex: CDI + 2%)
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Distribution Settings */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-primary-400" />
                <h3 className="font-semibold text-surface-100">Configurações de Distribuição</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-surface-400 mb-1">Frequência</label>
                  <select
                    value={config.frequency}
                    onChange={(e) => setConfig({ ...config, frequency: e.target.value as YieldConfig['frequency'] })}
                    className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  >
                    {Object.entries(frequencyLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-surface-400 mb-1">Base de Cálculo</label>
                  <select
                    value={config.calculationBase}
                    onChange={(e) => setConfig({ ...config, calculationBase: e.target.value as 'NOMINAL' | 'MARKET' })}
                    className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  >
                    <option value="NOMINAL">Valor Nominal</option>
                    <option value="MARKET">Valor de Mercado</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Tax Settings */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-primary-400" />
                <h3 className="font-semibold text-surface-100">Configurações de Imposto</h3>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.taxWithholding}
                    onChange={(e) => setConfig({ ...config, taxWithholding: e.target.checked })}
                    className="w-5 h-5 rounded"
                  />
                  <div>
                    <p className="font-medium text-surface-100">Retenção de IR na Fonte</p>
                    <p className="text-sm text-surface-400">Aplicar tabela regressiva de IR</p>
                  </div>
                </label>

                {config.taxWithholding && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-surface-700">
                    <div>
                      <label className="block text-xs text-surface-400 mb-1">Até 180 dias</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={config.taxRates?.upTo180Days || 22.5}
                          onChange={(e) => setConfig({
                            ...config,
                            taxRates: { ...config.taxRates!, upTo180Days: parseFloat(e.target.value) }
                          })}
                          className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-surface-100 pr-8"
                          step={0.5}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-surface-400 mb-1">181-360 dias</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={config.taxRates?.upTo360Days || 20}
                          onChange={(e) => setConfig({
                            ...config,
                            taxRates: { ...config.taxRates!, upTo360Days: parseFloat(e.target.value) }
                          })}
                          className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-surface-100 pr-8"
                          step={0.5}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-surface-400 mb-1">361-720 dias</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={config.taxRates?.upTo720Days || 17.5}
                          onChange={(e) => setConfig({
                            ...config,
                            taxRates: { ...config.taxRates!, upTo720Days: parseFloat(e.target.value) }
                          })}
                          className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-surface-100 pr-8"
                          step={0.5}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-surface-400 mb-1">Acima de 720 dias</label>
                      <div className="relative">
                        <input
                          type="number"
                          value={config.taxRates?.above720Days || 15}
                          onChange={(e) => setConfig({
                            ...config,
                            taxRates: { ...config.taxRates!, above720Days: parseFloat(e.target.value) }
                          })}
                          className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-surface-100 pr-8"
                          step={0.5}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500">%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button type="submit" disabled={saveMutation.isPending}>
                <Save className="w-4 h-4 mr-2" />
                {saveMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </div>

            {saveMutation.isError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">Erro ao salvar configurações. Tente novamente.</p>
              </div>
            )}

            {saveMutation.isSuccess && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm text-green-400">Configurações salvas com sucesso!</p>
              </div>
            )}
          </form>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary */}
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Resumo</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Supply Total</span>
                <span className="text-surface-100">{asset.totalSupply.toLocaleString('pt-BR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Preço Unitário</span>
                <span className="text-surface-100">{formatCurrency(asset.unitPrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Valor Total</span>
                <span className="text-surface-100 font-medium">
                  {formatCurrency(asset.totalSupply * asset.unitPrice)}
                </span>
              </div>
              {config.yieldRate && (
                <>
                  <div className="pt-3 border-t border-surface-700">
                    <div className="flex justify-between">
                      <span className="text-surface-400">Rendimento Anual</span>
                      <span className="text-green-400 font-medium">{config.yieldRate}%</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Rendimento Estimado/Ano</span>
                    <span className="text-green-400">
                      {formatCurrency((asset.totalSupply * asset.unitPrice * (config.yieldRate / 100)))}
                    </span>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Manual Distribution */}
          {yieldConfig?.isActive && (
            <Card className="p-6">
              <h3 className="font-semibold text-surface-100 mb-4">Distribuição Manual</h3>
              <p className="text-sm text-surface-400 mb-4">
                Acione uma distribuição de rendimentos manualmente fora do cronograma.
              </p>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => triggerDistributionMutation.mutate()}
                disabled={triggerDistributionMutation.isPending}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                {triggerDistributionMutation.isPending ? 'Processando...' : 'Distribuir Agora'}
              </Button>
            </Card>
          )}

          {/* Distribution History */}
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Histórico de Distribuições</h3>
            {distributions && distributions.length > 0 ? (
              <div className="space-y-3">
                {distributions.slice(0, 5).map((dist) => (
                  <div key={dist.id} className="p-3 bg-surface-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-surface-100">{dist.period}</span>
                      <Badge variant={
                        dist.status === 'COMPLETED' ? 'success' :
                        dist.status === 'FAILED' ? 'error' : 'warning'
                      } className="text-xs">
                        {dist.status === 'COMPLETED' ? 'Concluído' :
                         dist.status === 'FAILED' ? 'Falhou' :
                         dist.status === 'DISTRIBUTING' ? 'Distribuindo' : 'Pendente'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-surface-400">
                      <span>{dist.totalHolders} holders</span>
                      <span className="text-green-400">{formatCurrency(dist.totalAmount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <Clock className="w-8 h-8 text-surface-600 mx-auto mb-2" />
                <p className="text-sm text-surface-400">Nenhuma distribuição realizada</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default YieldConfiguration;
